
import React from 'react';
import { flushSync } from 'react-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import useRecordStore from 'contexts/record-store';
import ConfirmDialog from 'components/confirm-dialog';

const DeleteRecordDialog = ({ recordId, recordType, onClose, setIsLoading }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation('general');
  const recordStore = useRecordStore();
  const record = recordStore.useRecord(recordType, recordId).ornull;
  const [confirmDelete, setConfirmDelete] = React.useState();
  const typeName = Types.Record.getName(t, recordType);
  const recordName = record?.get()?.[recordType === Types.Record.DEPLOYMENT ? 'caseNumber' : 'name'] || 'unnamed';
  const countExercises = record?.get()?.exercises?.length || 0;
  
  React.useEffect(() => {
    const deleteRecord = async () => {
      const confirmed = await confirmDelete();
      if (!confirmed) {
        setTimeout(() => onClose(), 100);
        return;
      }
  
      setIsLoading(true);
      try {
        let response = null;
        switch (recordType) {
          case Types.Record.EVENT:
            ({ response } = await recordStore.deleteEvent(recordId));
            break;
          case Types.Record.EXERCISE:
            ({ response } = await recordStore.deleteExercise(recordId));
            break;
          case Types.Record.DEPLOYMENT:
            ({ response } = await recordStore.deleteDeployment(recordId));
            break;
          case Types.Record.CLASS:
            ({ response } = await recordStore.deleteClass(recordId));
            break;
          case Types.Record.VET_VISIT:
            ({ response } = await recordStore.deleteVetVisit(recordId));
            break;
          default:
            throw new Error('invalid recordType prop');
        }
  
        if (response?.isSuccess) {
          enqueueSnackbar(`${typeName} deleted`, { variant: 'success'});
        }
  
      } catch (e) {
        let errmsg = `Error deleting ${typeName.toLowerCase()}: ${e.message}`;
        if (e.response && e.response.status === 403) {
          errmsg = `You are not authorized to delete this ${typeName.toLowerCase()}`;
        }
  
        enqueueSnackbar(errmsg, {
          variant: 'error',
          persist: true,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        });
  
      } finally {
        flushSync(() => {
          setIsLoading(false);
        });
        setTimeout(() => onClose(), 100);
      }
    }

    if (confirmDelete) {
      deleteRecord();
    }

  // recordStore is a setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete, onClose, recordId, recordType, setIsLoading, typeName, enqueueSnackbar]);

  
  const content = () => {
    switch (recordType) {
      case Types.Record.EVENT:
        return `Permanently delete the event "${recordName}"? `
          + `${countExercises > 0 ? `The ${countExercises} exercise${countExercises > 1 ? 's' : ''} associated with this event will also be deleted. ` : ''}`
          + 'This action cannot be undone.';
      case Types.Record.EXERCISE:
        return `Permanently delete the exercise "${recordName}"? This action cannot be undone.`;
      case Types.Record.DEPLOYMENT:
        return `Permanently delete the deployment "${recordName}"? This action cannot be undone.`;
      case Types.Record.CLASS:
        return `Permanently delete the class "${recordName}"? This action cannot be undone.`;
      case Types.Record.VET_VISIT:
        return `Permanently delete the vet visit "${recordName}"? This action cannot be undone.`;
      default:
        return '';
    }
  }


  const dialogActions = [
    {
      label: 'Cancel',
      value: false,
      isNotPriority: true,
    },
    {
      label: 'Delete',
      value: true,
    },
  ];


  return (
    <>
    { record &&
      <ConfirmDialog
        title={`Delete ${typeName}`}
        titleStyle="error"
        titleIcon="warning"
        content={content()}
        setConfirm={setConfirmDelete}
        customActions={dialogActions}
      />
    }
    </>
  )
}

export default DeleteRecordDialog;